import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ForumIcon from '@mui/icons-material/Forum';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SendIcon from '@mui/icons-material/Send';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useContactmomentenVan } from '../../api/contactmomenten.ts';
import { useMatchesVoorVacature } from '../../api/matches.ts';
import type { VacatureDetails, Vacaturestatus } from '../../api/typen.ts';
import { useVacature, useVacatureMutaties } from '../../api/vacatures.ts';
import { useVoordrachtenVanVacature } from '../../api/voordrachten.ts';
import { useSessie } from '../../auth/SessieContext.tsx';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { ContactmomentDialoog } from '../../componenten/ContactmomentDialoog.tsx';
import { ContactmomentenLijst } from '../../componenten/ContactmomentenLijst.tsx';
import { Kenmerken } from '../../componenten/Kenmerken.tsx';
import { locatieLabel } from '../../componenten/keuzevelden.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { MatchesTabel } from '../../componenten/MatchesTabel.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { VoordrachtDialoog } from '../../componenten/VoordrachtDialoog.tsx';
import { VoordrachtenTabel } from '../../componenten/VoordrachtenTabel.tsx';
import { datum, ofStreepje, salaris, uren, volledigeNaam } from '../../helpers/formatteer.ts';
import {
  dienstverbandLabels,
  eissoortLabels,
  label,
  opleidingsniveauLabels,
  opties,
  taalnaam,
  taalniveauLabels,
  vaardigheidsniveauLabels,
  vacaturestatusLabels,
} from '../../helpers/labels.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { IndelingFormulier } from '../kandidaten/IndelingFormulier.tsx';
import { EisenFormulier } from './EisenFormulier.tsx';
import { KlantKoppelingFormulier } from './KlantKoppelingFormulier.tsx';
import { VacatureFormulier } from './VacatureFormulier.tsx';

const tabs = ['vacature', 'voordrachten', 'matches', 'gesprekken'] as const;
type Tabnaam = (typeof tabs)[number];
const statusOpties = opties(vacaturestatusLabels);

function Kaart({ titel, actie, children }: { titel: string; actie?: ReactNode | undefined; children: ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={titel} action={actie} slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function eisOmschrijving(eis: VacatureDetails['eisen'][number]): string {
  switch (eis.soort) {
    case 'ervaring':
      return `${eis.functietitel?.naam ?? '?'}${eis.minimaalJaren ? `, minimaal ${eis.minimaalJaren} jaar` : ''}`;
    case 'vaardigheid':
      return `${eis.vaardigheid?.naam ?? '?'}${eis.minimaalVaardigheidsniveau ? `, minimaal ${vaardigheidsniveauLabels[eis.minimaalVaardigheidsniveau]}` : ''}`;
    case 'certificaat':
      return eis.certificaattype?.naam ?? '?';
    case 'opleiding':
      return label(opleidingsniveauLabels, eis.opleidingsniveau);
    case 'taal':
      return `${eis.taalcode ? taalnaam(eis.taalcode) : '?'}${eis.minimaalTaalniveau ? `, minimaal ${taalniveauLabels[eis.minimaalTaalniveau]}` : ''}`;
    case 'rijbewijs':
      return `Rijbewijs ${eis.rijbewijs ?? '?'}`;
  }
}

function StatusKnop({ vacature }: { vacature: VacatureDetails }) {
  const mutaties = useVacatureMutaties();
  const { meld, meldFout } = useMeldingen();
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const kies = (status: Vacaturestatus) => {
    setAnker(null);
    if (status !== vacature.status) {
      mutaties.zetStatus
        .mutateAsync({ id: vacature.id, status })
        .then(() => meld(`Status is nu ${vacaturestatusLabels[status].toLowerCase()}`))
        .catch(meldFout);
    }
  };
  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        onClick={(gebeurtenis) => setAnker(gebeurtenis.currentTarget)}
        loading={mutaties.zetStatus.isPending}
      >
        Status: {vacaturestatusLabels[vacature.status]}
      </Button>
      <Menu anchorEl={anker} open={Boolean(anker)} onClose={() => setAnker(null)}>
        {statusOpties.map((optie) => (
          <MenuItem key={optie.waarde} selected={optie.waarde === vacature.status} onClick={() => kies(optie.waarde)}>
            {optie.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function VacatureTab({ vacature }: { vacature: VacatureDetails }) {
  const mutaties = useVacatureMutaties();
  const { meld, meldFout } = useMeldingen();
  const [koppelen, setKoppelen] = useState(false);
  const [ontkoppelen, setOntkoppelen] = useState(false);
  const [eisen, setEisen] = useState(false);
  const [indeling, setIndeling] = useState(false);

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Kaart titel="Gegevens">
            <Kenmerken
              items={[
                { label: 'Dienstverband', waarde: label(dienstverbandLabels, vacature.dienstverband) },
                { label: 'Uren per week', waarde: uren(vacature.urenMin, vacature.urenMax) },
                { label: 'Salaris', waarde: salaris(vacature.salarisMin, vacature.salarisMax) },
                { label: 'Startdatum', waarde: datum(vacature.startdatum) },
                { label: 'Werklocatie', waarde: vacature.locatie ? locatieLabel(vacature.locatie) : null },
                {
                  label: 'Bron',
                  waarde: vacature.bronUrl ? (
                    <Link href={vacature.bronUrl} target="_blank" rel="noreferrer">
                      {vacature.bron ?? vacature.bronUrl}
                    </Link>
                  ) : (
                    ofStreepje(vacature.bron)
                  ),
                },
                { label: 'Aangemaakt', waarde: datum(vacature.aangemaaktOp) },
                { label: 'Gewijzigd', waarde: datum(vacature.gewijzigdOp) },
              ]}
            />
            {vacature.omschrijving && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
                {vacature.omschrijving}
              </Typography>
            )}
          </Kaart>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <Kaart
              titel="Klant"
              actie={
                <Stack direction="row">
                  <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setKoppelen(true)}>
                    {vacature.bedrijf ? 'Wijzigen' : 'Koppelen'}
                  </Button>
                  {vacature.bedrijf && (
                    <Button size="small" startIcon={<LinkOffIcon />} onClick={() => setOntkoppelen(true)}>
                      Ontkoppelen
                    </Button>
                  )}
                </Stack>
              }
            >
              {vacature.bedrijf ? (
                <Kenmerken
                  items={[
                    {
                      label: 'Bedrijf',
                      waarde: (
                        <Link component={RouterLink} to={`/bedrijven/${vacature.bedrijf.id}`} underline="hover">
                          {vacature.bedrijf.naam}
                        </Link>
                      ),
                    },
                    {
                      label: 'Contactpersoon',
                      waarde: vacature.contactpersoon
                        ? [volledigeNaam(vacature.contactpersoon), vacature.contactpersoon.functie]
                            .filter(Boolean)
                            .join(', ')
                        : null,
                    },
                    { label: 'E-mail contactpersoon', waarde: vacature.contactpersoon?.email },
                    { label: 'Telefoon contactpersoon', waarde: vacature.contactpersoon?.telefoon },
                  ]}
                />
              ) : (
                <Typography color="text.secondary">Nog niet aan een bedrijf gekoppeld.</Typography>
              )}
            </Kaart>
            <Kaart
              titel="Indeling"
              actie={
                <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setIndeling(true)}>
                  Bewerken
                </Button>
              }
            >
              <Kenmerken
                kolommen={1}
                items={[
                  {
                    label: 'Functietitels',
                    waarde: vacature.functietitels.length ? (
                      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {vacature.functietitels.map((k) => (
                          <Chip key={k.functietitelId} label={k.functietitel.naam} size="small" />
                        ))}
                      </Stack>
                    ) : null,
                  },
                  {
                    label: 'Talentpools',
                    waarde: vacature.talentpools.length ? (
                      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {vacature.talentpools.map((k) => (
                          <Chip
                            key={k.talentpoolId}
                            label={k.talentpool.naam}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    ) : null,
                  },
                ]}
              />
            </Kaart>
          </Stack>
        </Grid>
        <Grid size={12}>
          <Kaart
            titel="Eisen en wensen"
            actie={
              <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setEisen(true)}>
                Bewerken
              </Button>
            }
          >
            {vacature.eisen.length === 0 ? (
              <Typography color="text.secondary">Nog geen eisen vastgelegd.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Soort</TableCell>
                    <TableCell>Eis</TableCell>
                    <TableCell>Verplicht</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vacature.eisen.map((eis) => (
                    <TableRow key={eis.id}>
                      <TableCell>{eissoortLabels[eis.soort]}</TableCell>
                      <TableCell>{eisOmschrijving(eis)}</TableCell>
                      <TableCell>
                        <Chip
                          label={eis.verplicht ? 'Harde eis' : 'Wens'}
                          size="small"
                          color={eis.verplicht ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Kaart>
        </Grid>
      </Grid>
      <KlantKoppelingFormulier open={koppelen} onSluit={() => setKoppelen(false)} vacature={vacature} />
      <EisenFormulier open={eisen} onSluit={() => setEisen(false)} vacature={vacature} />
      <IndelingFormulier
        open={indeling}
        onSluit={() => setIndeling(false)}
        functietitels={vacature.functietitels.map((k) => k.functietitel)}
        talentpools={vacature.talentpools.map((k) => k.talentpool)}
        opslaan={(invoer) => mutaties.zetIndeling.mutateAsync({ id: vacature.id, invoer })}
      />
      <BevestigDialoog
        open={ontkoppelen}
        titel="Klant ontkoppelen"
        bevestigLabel="Ontkoppelen"
        bezig={mutaties.ontkoppelBedrijf.isPending}
        onSluit={() => setOntkoppelen(false)}
        onBevestig={() =>
          mutaties.ontkoppelBedrijf
            .mutateAsync({ id: vacature.id })
            .then(() => {
              meld('Klant ontkoppeld');
              setOntkoppelen(false);
            })
            .catch(meldFout)
        }
      >
        De vacature wordt losgemaakt van {vacature.bedrijf?.naam} en de contactpersoon.
      </BevestigDialoog>
    </>
  );
}

function VoordrachtenTab({ vacature }: { vacature: VacatureDetails }) {
  const voordrachten = useVoordrachtenVanVacature(vacature.id);
  return (
    <Laadstatus query={voordrachten} leeg="Nog geen voordrachten op deze vacature.">
      {(lijst) => <VoordrachtenTabel voordrachten={lijst} toon="kandidaat" />}
    </Laadstatus>
  );
}

function MatchesTab({ vacature }: { vacature: VacatureDetails }) {
  const matches = useMatchesVoorVacature(vacature.id, { limiet: 100 });
  return (
    <Laadstatus query={matches} leeg="Nog geen matches berekend voor deze vacature.">
      {(lijst) => <MatchesTabel matches={lijst} vast={{ vacature }} />}
    </Laadstatus>
  );
}

function GesprekkenTab({ vacature }: { vacature: VacatureDetails }) {
  const { offset, limiet, zetOffset } = useZoekparameters(25);
  const gesprekken = useContactmomentenVan('vacature', vacature.id, { limiet, offset });
  return (
    <ContactmomentenLijst
      query={gesprekken}
      toon={{ vacature: false }}
      paginering={{ offset, limiet, onOffset: zetOffset }}
    />
  );
}

/**
 * De pagina van een vacature: gegevens, klant, eisen, indeling, voordrachten, matches en gesprekken.
 * @returns De pagina.
 */
export function VacaturePagina() {
  const { id = '' } = useParams();
  const vacature = useVacature(id);
  const { krijg, zet } = useZoekparameters();
  const tab = (tabs as readonly string[]).includes(krijg('tab')) ? (krijg('tab') as Tabnaam) : 'vacature';
  const { isBeheerder } = useSessie();
  const mutaties = useVacatureMutaties();
  const { meld, meldFout } = useMeldingen();
  const navigeer = useNavigate();
  const [bewerken, setBewerken] = useState(false);
  const [gesprek, setGesprek] = useState(false);
  const [voordragen, setVoordragen] = useState(false);
  const [verwijderen, setVerwijderen] = useState(false);

  return (
    <Laadstatus query={vacature}>
      {(details) => (
        <Pagina
          titel={details.titel}
          terug={{ naar: '/vacatures', label: 'Vacatures' }}
          ondertitel={
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
              <StatusChip soort="vacature" waarde={details.status} />
              {details.bedrijf && (
                <Link component={RouterLink} to={`/bedrijven/${details.bedrijf.id}`} underline="hover">
                  {details.bedrijf.naam}
                </Link>
              )}
              {details.locatie && <span>{locatieLabel(details.locatie)}</span>}
            </Stack>
          }
          acties={
            <>
              <StatusKnop vacature={details} />
              <Button variant="outlined" startIcon={<ForumIcon />} onClick={() => setGesprek(true)}>
                Gesprek vastleggen
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setVoordragen(true)}
                disabled={details.status !== 'open'}
              >
                Voordragen
              </Button>
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setBewerken(true)}>
                Bewerken
              </Button>
              {isBeheerder && (
                <Button color="error" startIcon={<DeleteOutlinedIcon />} onClick={() => setVerwijderen(true)}>
                  Verwijderen
                </Button>
              )}
            </>
          }
        >
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_gebeurtenis, waarde: Tabnaam) => zet({ tab: waarde === 'vacature' ? undefined : waarde })}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab value="vacature" label="Vacature" />
              <Tab value="voordrachten" label="Voordrachten" />
              <Tab value="matches" label="Matches" />
              <Tab value="gesprekken" label="Gesprekken" />
            </Tabs>
          </Paper>
          {tab === 'vacature' && <VacatureTab vacature={details} />}
          {tab === 'voordrachten' && (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Voordrachten</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setVoordragen(true)}
                  disabled={details.status !== 'open'}
                >
                  Voordragen
                </Button>
              </Stack>
              <VoordrachtenTab vacature={details} />
            </Paper>
          )}
          {tab === 'matches' && (
            <Paper sx={{ p: 2 }}>
              <MatchesTab vacature={details} />
            </Paper>
          )}
          {tab === 'gesprekken' && (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Gesprekken</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setGesprek(true)}>
                  Gesprek vastleggen
                </Button>
              </Stack>
              <GesprekkenTab vacature={details} />
            </Paper>
          )}

          <VacatureFormulier open={bewerken} onSluit={() => setBewerken(false)} bestaand={details} />
          <ContactmomentDialoog
            open={gesprek}
            onSluit={() => setGesprek(false)}
            vacature={details}
            bedrijfId={details.bedrijfId}
          />
          <VoordrachtDialoog
            open={voordragen}
            onSluit={() => setVoordragen(false)}
            vacature={details}
            onGemaakt={(voordracht) => void navigeer(`/voordrachten/${voordracht.id}`)}
          />
          <BevestigDialoog
            open={verwijderen}
            titel="Vacature verwijderen"
            bevestigLabel="Verwijderen"
            gevaarlijk
            bezig={mutaties.verwijder.isPending}
            onSluit={() => setVerwijderen(false)}
            onBevestig={() =>
              mutaties.verwijder
                .mutateAsync(details.id)
                .then(() => {
                  meld('Vacature verwijderd');
                  void navigeer('/vacatures');
                })
                .catch(meldFout)
            }
          >
            De vacature {details.titel} wordt verwijderd. Dit kan niet zolang er voordrachten op staan.
          </BevestigDialoog>
        </Pagina>
      )}
    </Laadstatus>
  );
}
